import { useEffect, useRef } from "react";
import { call } from "../lib/callFunction";

// Streams the tech's position to techLocations while `active` — the web
// version of courierHop's on-duty tracking (foreground only; the Flutter app
// will carry background duty). Throttled to one ping per 15s or 75 m moved,
// and a final off-duty ping on stop.

const MIN_INTERVAL_MS = 15_000;
const MIN_MOVE_M = 75;
const MAX_QUIET_MS = 60_000;

function meters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function useLocationSharing(tenantId: string | null, active: boolean) {
  const last = useRef<{ at: number; lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!tenantId || !active || !("geolocation" in navigator)) return;
    let stopped = false;

    const send = (pos: GeolocationPosition, onDuty: boolean) => {
      last.current = { at: Date.now(), lat: pos.coords.latitude, lng: pos.coords.longitude };
      void call("updateMyLocation", {
        tenantId,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? null,
        heading: Number.isFinite(pos.coords.heading) ? pos.coords.heading : null,
        speedMps: Number.isFinite(pos.coords.speed) ? pos.coords.speed : null,
        onDuty,
      }).catch(() => undefined); // a dropped ping is fine, the next one lands
    };

    const watch = navigator.geolocation.watchPosition(
      (pos) => {
        if (stopped) return;
        const prev = last.current;
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (prev) {
          const age = Date.now() - prev.at;
          if (age < MIN_INTERVAL_MS) return;
          if (meters(prev, here) < MIN_MOVE_M && age < MAX_QUIET_MS) return;
        }
        send(pos, true);
      },
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 20_000 },
    );

    return () => {
      stopped = true;
      navigator.geolocation.clearWatch(watch);
      // One last ping marks the tech off duty so the map grays them out.
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos, false),
        () => undefined,
        { maximumAge: 60_000, timeout: 5_000 },
      );
    };
  }, [tenantId, active]);
}
